import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface CrisprStep {
  name: string;
  nameEn: string;
  icon: string;
  description: string;
  detail: string;
  color: string;
}

const STEPS: CrisprStep[] = [
  {
    name: '设计向导RNA',
    nameEn: 'Design sgRNA',
    icon: '🧬',
    description: '设计与目标DNA序列互补的单链向导RNA',
    detail: '向导RNA（sgRNA）长约20个核苷酸，精确匹配目标基因位点',
    color: '#4ECDC4',
  },
  {
    name: 'Cas9识别靶点',
    nameEn: 'Cas9 Targeting',
    icon: '🔍',
    description: 'Cas9蛋白携带sgRNA在基因组中搜索匹配序列',
    detail: '需要识别PAM序列（NGG），确保切割特异性',
    color: '#667EEA',
  },
  {
    name: '双链DNA切割',
    nameEn: 'DSB Cleavage',
    icon: '✂️',
    description: 'Cas9核酸酶在目标位点制造双链断裂',
    detail: 'RuvC和HNH两个结构域分别切割DNA双链',
    color: '#FF6B35',
  },
  {
    name: '细胞修复',
    nameEn: 'DNA Repair',
    icon: '🔧',
    description: '细胞启动修复机制：NHEJ或HDR',
    detail: 'NHEJ → 基因敲除 | HDR + 模板 → 精确编辑',
    color: '#2ECC71',
  },
  {
    name: '基因编辑完成',
    nameEn: 'Gene Edited',
    icon: '✅',
    description: '目标基因被成功修改，获得预期表型',
    detail: '可用于疾病治疗、作物改良、基因功能研究等',
    color: '#9B59B6',
  },
];

const APPLICATIONS = [
  { name: '遗传病治疗', icon: '🏥', desc: '镰状细胞贫血、β-地中海贫血' },
  { name: '癌症免疫', icon: '🛡️', desc: 'CAR-T细胞改造、免疫检查点' },
  { name: '农业育种', icon: '🌾', desc: '抗病虫害、提高产量和营养' },
  { name: '基因诊断', icon: '🔬', desc: '快速检测病原体（如新冠）' },
];

const DNAHelix: React.FC<{ frame: number; opacity: number }> = ({ frame, opacity }) => {
  const pairs = 20;
  return (
    <svg width={180} height={600} style={{ opacity }}>
      {Array.from({ length: pairs }).map((_, i) => {
        const y = i * 30 + 10;
        const phase = (i * 0.5 + frame * 0.03) % (Math.PI * 2);
        const x1 = 90 + Math.sin(phase) * 50;
        const x2 = 90 - Math.sin(phase) * 50;
        const depth = Math.cos(phase);

        return (
          <g key={i}>
            <circle cx={x1} cy={y} r={5} fill={depth > 0 ? '#4ECDC4' : '#4ECDC466'} />
            <circle cx={x2} cy={y} r={5} fill={depth > 0 ? '#FF6B3566' : '#FF6B35'} />
            <line
              x1={x1}
              y1={y}
              x2={x2}
              y2={y}
              stroke="rgba(255,255,255,0.15)"
              strokeWidth={2}
              strokeDasharray="4 3"
            />
          </g>
        );
      })}
    </svg>
  );
};

export const CRISPRGeneEditAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleProg = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });

  const currentStep = Math.min(
    Math.floor(Math.max(0, frame - 50) / 90),
    STEPS.length - 1,
  );

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(ellipse at 20% 50%, #0a192f 0%, #060d1a 100%)',
        fontFamily: 'system-ui, -apple-system, PingFang SC, sans-serif',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', left: 20, top: 270 }}>
        <DNAHelix frame={frame} opacity={0.3} />
      </div>

      <div
        style={{
          position: 'absolute',
          top: 36,
          left: 220,
          opacity: titleProg,
          transform: `translateY(${interpolate(titleProg, [0, 1], [-30, 0])}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 64,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #4ECDC4, #667EEA)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          CRISPR 基因编辑
        </h1>
        <p style={{ fontSize: 30, margin: '6px 0 0', color: 'rgba(255,255,255,0.4)' }}>
          CRISPR-Cas9 · 改写生命密码的分子剪刀
        </p>
      </div>

      {/* Step timeline */}
      <div style={{ position: 'absolute', left: 220, top: 290, right: 55 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 80 }}>
          {STEPS.map((step, i) => {
            const stepProg = spring({
              frame: frame - 20 - i * 10,
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const isActive = i === currentStep;

            return (
              <div
                key={step.nameEn}
                style={{
                  flex: 1,
                  opacity: stepProg,
                  transform: `translateY(${interpolate(stepProg, [0, 1], [15, 0])}px)`,
                }}
              >
                <div
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: isActive ? step.color : `${step.color}30`,
                    marginBottom: 18,
                    boxShadow: isActive ? `0 0 12px ${step.color}60` : 'none',
                  }}
                />
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? step.color : 'rgba(255,255,255,0.3)',
                    textAlign: 'center',
                  }}
                >
                  {step.icon} {step.name}
                </div>
              </div>
            );
          })}
        </div>

        {/* Current step detail */}
        {STEPS.map((step, i) => {
          const phaseStart = 50 + i * 90;
          const phaseEnd = phaseStart + 85;
          const fadeIn = interpolate(frame, [phaseStart, phaseStart + 18], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const fadeOut = interpolate(frame, [phaseEnd - 12, phaseEnd], [1, 0], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          const opacity = i === STEPS.length - 1 ? fadeIn : fadeIn * fadeOut;

          if (frame < phaseStart || opacity <= 0) return null;

          return (
            <div
              key={step.nameEn}
              style={{
                padding: '36px 40px',
                background: `${step.color}08`,
                border: `2px solid ${step.color}30`,
                borderRadius: 22,
                opacity,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
                <span style={{ fontSize: 50 }}>{step.icon}</span>
                <div>
                  <h2 style={{ fontSize: 42, fontWeight: 700, color: step.color, margin: 0 }}>
                    Step {i + 1}: {step.name}
                  </h2>
                  <span style={{ fontSize: 30, color: 'rgba(255,255,255,0.35)' }}>
                    {step.nameEn}
                  </span>
                </div>
              </div>

              <p
                style={{
                  fontSize: 30,
                  color: '#E5E7EB',
                  lineHeight: 1.6,
                  margin: '0 0 20px',
                }}
              >
                {step.description}
              </p>

              <div
                style={{
                  padding: '16px 24px',
                  background: 'rgba(0,0,0,0.3)',
                  borderRadius: 14,
                  borderLeft: `4px solid ${step.color}`,
                }}
              >
                <p style={{ fontSize: 28, color: '#9CA3AF', margin: 0, lineHeight: 1.6 }}>
                  🔬 {step.detail}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Applications */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 220,
          right: 55,
          display: 'flex',
          gap: 14,
        }}
      >
        {APPLICATIONS.map((app, i) => {
          const appProg = spring({
            frame: frame - 460 - i * 10,
            fps,
            config: { damping: 14, stiffness: 120 },
          });

          return (
            <div
              key={app.name}
              style={{
                flex: 1,
                padding: '12px 16px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 12,
                opacity: appProg,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}
            >
              <span style={{ fontSize: 34 }}>{app.icon}</span>
              <div>
                <div style={{ fontSize: 28, fontWeight: 600, color: '#4ECDC4' }}>{app.name}</div>
                <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.35)' }}>{app.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};
